import './CampaignDefinitionResults.scss';
import 'ag-grid-community/styles/ag-grid.css'; // Core grid CSS, always needed
import 'ag-grid-community/styles/ag-theme-alpine.css'; // Optional theme CSS
import Button from "@mui/material/Button";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {Autocomplete, Checkbox, FormControlLabel, TextField} from "@mui/material";
import CampaignDefinitionTextField from "./CampaignDefinitionTextField";
import {AgGridReact} from "ag-grid-react";
import { currencyFormatter, dateFormatter, DATE_FORMAT, probabilityFormatter, twoDecimalFormatter } from "../../../common/Utils";
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import {priorities} from "../../../constants/picklists";
import ConfirmPriorityDialog from "./ConfirmPriorityDialog";
import {IDIConfirmationDialog} from "../../IDIConfirmationDialog/IDIConfirmationDialog";
import { debounce } from 'lodash';
import {
  getSalesforceUsers,
  submitCampaign,
  submitForResearch,
  submitPayloadToIdiCore,
  exportPropertyLeadsAsCsv, exportOwnerLeadsAsCsv
} from "../../../../main/API/leadMakerSearchApi";
import CampaignDefinitionSelectField from "./CampaignDefinitionSelectField";
import {getCampaign} from "../../../../main/API/leadMakerSearchApi";
import * as Yup from "yup";
import {useSelector} from "react-redux";
import {
  selectActiveSearchParams,
  selectGeographicResultType, selectGridBlockSize, setGridBlockSize
} from "../../../../main/store/slices/leadMakerSearchSlice";
import {
  leadMakerSearchResultsDatasourceFn
} from "./CampaignDefinitionResultsDatasource";

import {setToastMessage} from "../../../../main/store/slices/utilSlice";
import toastTypes from "../../../constants/toastTypes";
import {useDispatch} from "react-redux";
import {geographicResultType} from "../../../constants/GeographicSearchConstants";

const CampaignDefinitionResults = () => {
  
  const dispatch = useDispatch();
  
  const bufferRows = 10;
  
  const submitTypes = {
    ALL_RESEARCH : 0,
    OWNER_RESEARCH : 1,
    ROOF_RESEARCH : 2,
    IDI_CORE: 3
  }

  const defaultPriority = priorities[2].id;

  const clearedPayload = {
    campaignId : "",
    campaignName : "",
    salesforceUserId : "",
    addOtherSalesRepsOwnedLeadsToCampaign : false,
  }
  
  const allColumns = [
    {field: 'leadName', headerName: 'Lead Name', flex: 2, minWidth: 160, checkboxSelection: true },
    {field: 'leadCompany', headerName: 'Lead Company', flex: 2, minWidth: 150 },
    {field: 'ownerState', headerName: 'Owner State', width: 80, cellClass: "grid-cell-centered" },
    {field: 'propertyCity', headerName: 'Property City', flex: 1, minWidth: 100, cellClass: "grid-cell-centered"},
    {field: 'propertyState', headerName: 'Property State', width: 110, cellClass: "grid-cell-centered" },
    {field: 'roofSquareFeet', headerName: 'Roof SF', width: 100, cellClass: "grid-cell-centered" },
    {field: 'roofType', headerName: 'Roof Type', width: 100, cellClass: "grid-cell-centered" },
    {field: 'maxHailSize', headerName: 'Hail Size At Location', width: 125, cellClass: "grid-cell-centered",
                        valueFormatter: twoDecimalFormatter },
    {field: 'ttlProperties', headerName: 'TTL Properties', width: 105, cellClass: "grid-cell-centered" },
    {field: 'propertyValue', headerName: 'Property Value', width: 110, cellClass: "grid-cell-right",
      valueFormatter: currencyFormatter },
    {field: 'maxSizeStormDate', headerName: 'Storm Date (Max Size)', width: 120, cellClass: 'grid-cell-centered', valueFormatter: dateFormatter(DATE_FORMAT)},
    {field: 'maxHailSize', headerName: 'Hail Size (Max Size)', width: 100, cellClass: 'grid-cell-centered'},
    {field: 'maxSizeProbabilityOfDamage', headerName: 'Prob of Dmg (Max Size)', width: 120, cellClass: "grid-cell-centered",
      valueFormatter: probabilityFormatter},
    {field: 'maxSizeWeightedRestorationValue', headerName: 'Resto Value (Max Size)', width: 140,
      cellClass: "grid-cell-right", valueFormatter: currencyFormatter },
    {field: 'mostRecentStormDate', headerName: 'Storm Date (Most Recent)', width: 120, cellClass: 'grid-cell-centered', valueFormatter: dateFormatter(DATE_FORMAT)},
    {field: 'mostRecentHailSize', headerName: 'Hail Size (Most Recent)', width: 120, cellClass: 'grid-cell-centered'},
    {field: 'mostRecentSizeProbabilityOfDamage', headerName: 'Prob of Dmg (Most Recent)', width: 130,
      cellClass: "grid-cell-centered", valueFormatter: probabilityFormatter },
    {field: 'mostRecentSizeWeightedRestorationValue', headerName: 'Resto Value (Most Recent)', width: 140,
      cellClass: "grid-cell-right", valueFormatter: currencyFormatter },
  ]
    
  const checkIcon = <CheckBoxOutlineBlankIcon fontSize="small" />;
  const checkedIcon = <CheckBoxIcon fontSize="small" />;
  const [ selectAllChecked, setSelectAllChecked ] = useState(false);
  const [ dialogOpen, setDialogOpen ] = useState(false);
  const [ idiDialogOpen, setIdiDialogOpen ] = useState(false);
  const [ submittedRows, setSubmittedRows ] = useState([]);
  const [ submitDestination, setSubmitDestination ] = useState();
  const [ selectedPriority, setSelectedPriority ] = useState(defaultPriority);
  const [ salesforceUsers, setSalesforceUsers ]  = useState([]);
  const [ selectionCount, setSelectionCount ] = useState(0);
  
  const [payload, setPayload] = useState(clearedPayload);
  const [errors, setErrors] = useState({});

  const currentGeographicResultType = useSelector(selectGeographicResultType);

  const {
    searchParams,
    gridBlockSize
  } = useSelector((state) => ({
    searchParams: selectActiveSearchParams(state),
    gridBlockSize: selectGridBlockSize(state)
  }))

  const [datasource, setDatasource] = useState(null);
  const [totalRows, setTotalRows] = useState(0);
  
  // Reset the datasource when the search params change
  useEffect(() => {
    if (searchParams != null) {
      const datasource = leadMakerSearchResultsDatasourceFn(searchParams, setTotalRows)
      setDatasource(datasource);
      setSelectAllChecked(false);
      setSelectionCount(0);
    }
  }, [searchParams])
  
  const gridRef = useRef();
  const [columnDefs, setColumnDefs] = useState(allColumns);
  
  const defaultColDef = useMemo(() => ({
    sortable: true,
    resizable: true,
    wrapHeaderText: true,
    autoHeaderHeight: true,
  }));
  
  useEffect(() => {
    async function fetchSalesforceUsers() {
      const {data} = await getSalesforceUsers("");
      const results = [];
      data?.returnObject?.forEach((value) => {
        results.push({
          id: value.id,
          value: value.firstName.concat(" ", value.lastName),
        });
      });
      setSalesforceUsers(results?.sort(function(a,b) {
        return a.value.localeCompare(b.value);
      }));
    }

    fetchSalesforceUsers();
  }, []);

  const onSelectionChanged = debounce((params) => {
    setSelectionCount(params.api.getSelectedRows()?.length)
  })
  
  const handleChange = (value, fieldName) => {
    // console.log('new value is ', value);
    // console.log(fieldName + " was changed to: " + value);
      setPayload((payload) => ({...payload, [fieldName]: value }));
  }
  
  const validateCampaignId = async (campaignId) => {
    const campaignName = await getCampaign(campaignId.trim());
    if (campaignName) {
      setPayload((payload) => ({...payload, campaignName: campaignName}));
      const { campaignId, ...newErrors } = errors;
      setErrors(newErrors);
      return true;
    }
    else {
      setPayload((payload) => ({...payload, campaignName: "Not Found"}));
      setErrors({ ...errors, campaignId : "Campaign Id is not valid"});
      return false;
    }
  }
  
  const campaignSchema = Yup.object().shape({
    campaignId: Yup.string()
      .required("Campaign Id is required"),
    salesforceUserId: Yup.string()
      .required("Salesforce user must be specified")
  });
  
  const validatePayload = async () => {
    let newErrors = {};
    try {
      campaignSchema.validateSync(payload, {abortEarly: false});
    } catch (error) {
      newErrors = error?.inner.reduce((acc, cur) => {
        acc[cur.path] = cur.message;
        return acc;
      }, {});
    }
    // only validate campaignId if everything else is good
    if (Object.keys(newErrors).length === 0) {
      let campaignIdGood = await validateCampaignId(payload.campaignId);
      console.log('campaignIdGood: ', campaignIdGood);
      if (!campaignIdGood) {
        newErrors = {
          campaignId: "Must enter a valid CampaignId",
        }
      }
    }
    return newErrors;
  }
  
  const submitCampaignPayload = async (payload) => {
    const campaignPayload = gridRef.current.api.getSelectedRows()
      .map(row => ({
        ownerId: row.ownerId,
        propertyId: row.propertyId,
        campaignId: payload.campaignId,
        salesforceUserId: payload.salesforceUserId,
        propertySalesforceId: row.salesforceId,
        addOtherSalesRepsOwnedLeadsToCampaign: payload.AddOtherSalesRepsOwnedLeadsToCampaign,
      }));
    let response = {};
    try {
      response = await submitCampaign(campaignPayload);
    } catch(error) {
      console.error(`Error: ${error}`);
      return {
        success: false,
        exceptionMessage : error.message,
      };
    }
    return response.data;
  }
  
  const submitSearchPayload =  async () => {
    // console.log('selected rows: ', gridRef.current.api.getSelectedRows());
    if (gridRef.current.api.getSelectedRows().length === 0) {
      dispatch(setToastMessage({
        message: `No properties selected for campaign`,
        type: toastTypes.error
      }))
      return
    }
    let formErrors = await validatePayload();
    if (!formErrors || (Object.keys(formErrors).length === 0)) {
      let responseData =  await submitCampaignPayload(payload);
      if (responseData.success) {
        dispatch(setToastMessage({
          message: `Leads and Assets queued for creation and Campaign population`,
          type: toastTypes.success
        }))
      } else {
        dispatch(setToastMessage({
          message: `Problem submitting leads/assets: ${responseData.exceptionMessage}`,
          type: toastTypes.error
        }))
      }
      
    } else {
      dispatch(setToastMessage({
        message: `Unable to submit leads/assets -- see errors in form`,
        type: toastTypes.error
      }))
    }
    setErrors(formErrors);
  }

  const submitSelectedRows = (dest) => {
    const selectedRows = gridRef.current.api.getSelectedRows();

    if (selectedRows && selectedRows.length > 0) {
      setSubmittedRows(selectedRows);
      setSubmitDestination(dest);
      setDialogOpen(true);
    }
    else {
      alert("No rows selected for submission");
    }
  };

  const submitToIdiCore = () => {
    const selectedRows = gridRef.current.api.getSelectedRows();
    if (selectedRows && selectedRows.length > 0) {
      setSubmittedRows(selectedRows);
      setIdiDialogOpen(true)
    } else {
      alert("No rows selected for submission");
    }
  }
  
  const submitConfirmed = async () => {
    const payload = submittedRows.map(row => {
        return {
          ownerId: row.ownerId,
          propertyId: row.propertyId,
          priorityLevel: parseInt(selectedPriority),
          researchType: submitDestination,
        }
    })

    submitForResearch(payload).then((response) => {
      const unsubmittedRowCount = response.data.returnObject.length;
      if(unsubmittedRowCount === 0) {
        dispatch(setToastMessage({
          type: 'success',
          message: `Successfully submitted ${submittedRows.length} properties for research`
        }))
      } else {
        dispatch(setToastMessage({
          type: 'warning',
          message: `${submittedRows.length - unsubmittedRowCount} properties were updated, but ${unsubmittedRowCount} were unable to be submitted.`
        }))
      }
    }).catch((error) => {
      dispatch(setToastMessage({
        type: 'error',
        message: `Error submitting for research: ${error.message}`
      }))
    });

    // Reset the selected priority to the default
    setSelectedPriority(defaultPriority);
  }

  const submitIdiConfirmed = async () => {
    const payload = {
      leadRequests: submittedRows.map(row => {
        return {
          leadId: row.ownerId,
          propertyId: row.propertyId
        }
      })
    }
    submitPayloadToIdiCore(payload).then(({ data }) => {
      if (data.success) {
        dispatch(setToastMessage({
          type: 'success',
          message: data.successMessage
        }))
      } else {
        dispatch(setToastMessage({
          type: 'error',
          message: "There was an error processing your request."
        }))
      }
    })
  }
  
  const exportGridAsCSV = async () => {
    const resultTypeId = parseInt(currentGeographicResultType);
    const selectedRows = gridRef.current.api.getSelectedRows();

    //If no rows are selected, alert the user and return early
    if(selectedRows.length === 0) {
        alert("No rows selected for export");
        return;
    }

    if(resultTypeId === geographicResultType.PROPERTY.id) {
      await exportPropertyLeadsAsCsv(selectedRows.map(row => row.propertyId));
    } else if(resultTypeId === geographicResultType.OWNER.id) {
      await exportOwnerLeadsAsCsv(selectedRows.map(row => row.ownerId));
    }
  }

  const handleForceLoadAllData = () => {
    // gridRef.current.api.setDatasource(undefined);
    setDatasource(undefined);
    setSelectAllChecked(false);
    dispatch(setGridBlockSize(totalRows));
    setTimeout(() => {
      setDatasource(leadMakerSearchResultsDatasourceFn(searchParams, setTotalRows))
    }, 500)
  }

  const handleSelectAll = (checked) => {
    setSelectAllChecked(checked);
    gridRef.current?.api.forEachNode(function (node, index) {
        node.setSelected(checked);
    });
  }
  
  const setColumns = (newValue) => {
    setColumnDefs(newValue);
  }
  
  return (
    <>
      <div className="campaign-definition-results-wrapper">
        <div className="form-row" >
          <div className="form-control row ">
            <div className="form-control row campaign-definition-button-row" >
              <div >
                <Autocomplete
                  multiple
                  id="select-columns"
                  options={allColumns}
                  value={columnDefs}
                  onChange={(event, newValue) => {
                    setColumns(newValue);
                  }}
                  size="small"
                  limitTags={1}
                  disableCloseOnSelect
                  isOptionEqualToValue={(option, value) => option.headerName === value.headerName}
                  getOptionLabel={(option) => option.headerName || ""}
                  renderOption={(props, option, { selected }) => (
                    <li {...props} style={{ height: "1.5em" }}>
                      <Checkbox
                        icon={checkIcon}
                        checkedIcon={checkedIcon}
                        style={{ marginRight: 2}}
                        size="small"
                        checked={selected}
                      />
                      {option.headerName}
                    </li>
                  )}
                  style={{ width: 300 }}
                  renderInput={(params) => {
                    return (
                    <TextField {...params}
                       size="small"
                       label="Select Columns"
                    />
                  )}}
                />
              </div>
              <div className="campaign-definition-button-container">
                <Button variant='outlined'
                        disabled={gridRef.current == null}
                        onClick={() => {exportGridAsCSV()}}
                        size="small">Export to CSV</Button>
                <Button variant='outlined'
                        disabled={gridRef.current == null}
                        onClick={() => {submitToIdiCore(submitTypes.IDI_CORE)}}
                        size="small"
                        color='error'>Submit to IDI Core</Button>
                <Button variant='outlined'
                        disabled={gridRef.current == null}
                        onClick={() => {submitSelectedRows(submitTypes.ALL_RESEARCH)}}
                        size="small"
                        color='error'>Submit for All Research</Button>
                <Button variant='outlined'
                        disabled={gridRef.current == null}
                        onClick={() => {submitSelectedRows(submitTypes.ROOF_RESEARCH)}}
                        size="small"
                        color='error'>Submit for Roof Research</Button>
                <Button variant='outlined'
                        disabled={gridRef.current == null}
                        onClick={() => {submitSelectedRows(submitTypes.OWNER_RESEARCH)}}
                        size="small"
                        color='error'>Submit for Owner Research</Button>
              </div>
            </div>
          </div>
          <ConfirmPriorityDialog
            open={dialogOpen}
            setOpen={setDialogOpen}
            title="Choose Priority for selected items:"
            setPriority={setSelectedPriority}
            onConfirm={submitConfirmed}
          />
          <IDIConfirmationDialog
            open={idiDialogOpen}
            setOpen={setIdiDialogOpen}
            title="Confirm Submission to IDI Core"
            selectionCount={submittedRows?.length}
            setPriority={setSelectedPriority}
            onConfirm={submitIdiConfirmed}
          />
          {datasource !== null && <div className="campaign-definition-select-all">
            <FormControlLabel
              control={<Checkbox checked={selectAllChecked}
                                 onChange={(e) =>
                                   handleSelectAll(e.target.checked, true)}/>}
              label={<span style={{ fontSize: "0.8rem"}}>Select All Loaded Records</span>}
              labelPlacement="end"
            />
            <Button variant="text" onClick={handleForceLoadAllData}>Force Load All Data</Button>
            {!!selectionCount && <span>Selected Count: {selectionCount}</span>}
          </div>}
          <div className="ag-theme-alpine form-control row" style={{ height: 320}}>
            {datasource !== null ?
            <AgGridReact
              ref={gridRef}
              rowHeight="30"
              columnDefs={columnDefs}
              defaultColDef={defaultColDef}
              suppressMultiSort
              suppressRowClickSelection={true}
              animateRows={true}
              rowSelection='multiple'
              rowModelType='infinite'
              rowBuffer={bufferRows}
              onSelectionChanged={onSelectionChanged}
              cacheBlockSize={gridBlockSize}
              datasource={datasource}
              onRowSelected={(params) => {
                if (!params.node.selected) {
                  setSelectAllChecked(false);
                }
              }}
              paginationPageSize={gridBlockSize}
            /> : null}
          </div>
        </div>
        <div className="form-control row" /><div>&nbsp;</div>
        <div className="form-control row campaign-definition-form-control-row" >
            <CampaignDefinitionTextField
              fieldName="campaignId"
              label="Salesforce Campaign"
              value={payload.campaignId}
              onChange={handleChange}
              error={errors.campaignId} 
            />
            <CampaignDefinitionSelectField
              id="salesforceUserId"
              defaultValue="Select User"
              label="Salesforce User"
              fieldName="salesforceUserId"
              value={payload.salesforceUserId}
              options={salesforceUsers}
              error={errors.salesforceUserId}
              onChange={handleChange}
            />
            <Button
              variant='outlined'
              size="small"
              onClick={submitSearchPayload}>
                Submit
            </Button>
        </div>
        <div className="form-control row campaign-definition-form-control-row" >
          <div className="campaign-definition-control-container flex-row" >
            <Button variant='outlined' onClick={() => validateCampaignId(payload.campaignId)} color='error' size="small">Validate Campaign</Button>
            <div style={{ width: "1em" }}>&nbsp;</div>
            <TextField
              variant="outlined"
              disabled
              value={payload.campaignName}
              size="small"
              inputProps={{ style : { width: "20em" }}}
            />
          </div>
          <div className="flx-row">
            <FormControlLabel
              control={<Checkbox checked={payload.AddOtherSalesRepsOwnedLeadsToCampaign}
                                onChange={(e) =>
                                  handleChange(e.target.checked, "AddOtherSalesRepsOwnedLeadsToCampaign")}/>}
              label={<span style={{ fontSize: "0.8rem"}}>Add Other Sales Reps Owned Leads to Campaign</span>}
              labelPlacement="end"
            />
          </div>
          {/* <div style={{ width: "3.5em" }}>&nbsp;</div> */}
          <Button variant='outlined'
                  size="small"
                  onClick={() => setPayload(clearedPayload)}>Clear</Button>
        </div>
      </div>
    </>
  );
}

export default CampaignDefinitionResults;

